import {
  faArrowRight,
  faFilter,
  faFlag,
  faTicket,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {
  GSListPage,
  GSEmptyList,
  GSPageSection,
  defaultPadding,
  defaultLayouts,
} from "golfstatus_react_components";
import React, { useEffect } from "react";
import { useMatch, useSearchParams } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTournamentPaymentSettings } from "../../../reducers/tournamentSlice";
import {
  clearNotifications,
  selectRegistrationOrder,
} from "../../../reducers/registrationOrderSlice";
import "./event-packages.scss";
import { useHeaderStyle, useSectionStyles } from "../../../hooks/themeHooks";
import CartStepItem from "./cart/cart-step-item";
import {
  getOrderItemCount,
  getPriceFormatted,
  getRegistrationSummary,
} from "../../../models/Registration";
import { getRouting } from "../../../helpers/Converters";
import { TOURNAMENT_ID, eventBaseRoute } from "../../../routes/event";
import PageHeader from "../../page-header";
import PackageFilter from "./packages/package-filter";
import PackageList, {
  ADD_ON_PACKAGES,
  PLAYER_PACKAGES,
  SPONSOR_PACKAGES,
  TEAM_PACKAGES,
} from "./packages/package-list";
import PageFooter from "./cart/page-footer";
import { useNavigationButtons } from "../../../hooks/pageHooks";
import { ACTIVE, usePermissions } from "../../../hooks/permissionHooks";
import {
  useLoading,
  useTournament,
  useTournamentPackages,
} from "../../../hooks/tournamentHooks";
import PackageAccessWidget from "../../widgets/package-access-widget";
import {
  BANK_ACCOUNT,
  CREDIT_CARD,
  CUSTOMER_INVOICE,
  getActivePaymentMethods,
  PAY_AT_COURSE,
} from "../../../forms/CartSections";
import { useNotificationBanner, useNotifications } from "../../../hooks/notificationHooks";
import { useGolfstatusNavigate } from "../../../hooks/navigationHooks";

const EventPackages = (props) => {
  const navigate = useGolfstatusNavigate();

  const route = getRouting(eventBaseRoute, [TOURNAMENT_ID, "packages", "*"]);
  const match = useMatch(route);

  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const filter = searchParams?.getAll("view") ?? "";

  const registrationOrder = useSelector(selectRegistrationOrder);
  const tournament = useTournament();
  const tournamentID = tournament?.id;

  useEffect(() => {
    if (tournamentID) {
      dispatch(getTournamentPaymentSettings(tournamentID));
    }
  }, [dispatch, tournamentID]);

  const publicUrl = tournament?.publicUrl;

  const { style, headerSectionStyle } = useSectionStyles();

  const [isLoading] = useLoading([
    "getTournamentPackages",
    "getPublicTournamentDetails",
  ]);

  const {
    teamPackages,
    playerPackages,
    sponsorPackages,
    addonPackages,
    hasAccessPackages,
    activePackages,
  } = useTournamentPackages(true);

  const getPageActions = useNavigationButtons("packages");

  const permissions = usePermissions();
  const packagesEnabled = permissions.packagesEnabled();
  const privateEvent = !permissions.publicRegistration;
  const enableAccessLists = true;

  const getSearchActions = () => {
    return [
      {
        id: 1,
        type: "mobile-icon",
        actionIcon: faFilter,
      },
    ];
  };

  const getEmptyActions = () => {
    const actions = [
      {
        title: "View Event Details",
        type: "grey",
        onClick: () => {
          navigate(getRouting(eventBaseRoute, [publicUrl]));
        },
      },
    ];
    if (permissions.donationsActive()) {
      actions.push({
        title: "Make a Donation",
        type: "black",
        onClick: () => {
          navigate(getRouting(eventBaseRoute, [publicUrl, "donate"]));
        },
      });
    }
    return actions;
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="No Packages Yet"
        detail={`${tournament?.name} does not have any packages available right now.`}
        actions={getEmptyActions()}
        style={defaultPadding.emptyListPad}
      />
    );
  };

  const isSidePanelOpen = () => {
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const getAvailablePackages = () => {
    let available = [];
    if (sponsorPackages?.length > 0) {
      available.push({
        label: "Sponsorships",
        icon: faFlag,
        id: "sponsor",
      });
    }
    if (teamPackages?.length > 0) {
      available.push({
        label: "Team Registrations",
        icon: faUsers,
        id: "team",
      });
    }
    if (playerPackages?.length > 0) {
      available.push({
        label: "Player Registrations",
        icon: faUser,
        id: "player",
      });
    }
    if (addonPackages?.length > 0) {
      available.push({
        label: "Add-Ons & Extras",
        icon: faTicket,
        id: "extras",
      });
    }
    return available;
  };

  const availablePackages = getAvailablePackages();
  const availablePackagesLength = availablePackages.length;
  const packageID = availablePackages?.[0]?.id
  const filterLength = filter.length;
  
  useEffect(() => {
    if (packagesEnabled || privateEvent) {
      if (availablePackagesLength === 1 && filterLength === 0) {
        navigate(`?view=${packageID}`, { replace: true });
      }
    }
  }, [
    navigate,
    availablePackagesLength,
    packageID,
    filterLength,
    packagesEnabled,
    privateEvent,
  ]);

  const getHeaderSection = () => {
    const body = [];

    if (getAvailablePackages()?.length > 1) {
      body.push(<PackageFilter filter={filter} />);
    }

    if (hasAccessPackages && enableAccessLists) {
      body.push(<PackageAccessWidget />);
    }

    return body;
  };

  const packageSections = (
    <>
      {getAvailablePackages()?.length !== 1 || hasAccessPackages ? (
        <GSPageSection {...headerSectionStyle} body={getHeaderSection()} />
      ) : null}

      {filter?.includes?.("sponsor") && sponsorPackages?.length > 0 ? (
        <GSPageSection
          title="Sponsorships"
          style={style}
          bodyStyle={{ ...defaultPadding.noPad, overflow: "visible" }}
          body={[
            <PackageList type={SPONSOR_PACKAGES} packages={sponsorPackages} />,
          ]}
        />
      ) : null}

      {filter?.includes?.("team") && teamPackages?.length > 0 ? (
        <GSPageSection
          title="Team Registrations"
          style={style}
          bodyStyle={{ ...defaultPadding.noPad, overflow: "visible" }}
          body={[<PackageList type={TEAM_PACKAGES} packages={teamPackages} />]}
        />
      ) : null}

      {filter?.includes?.("player") && playerPackages?.length > 0 ? (
        <GSPageSection
          title="Player Registrations"
          style={style}
          bodyStyle={{ ...defaultPadding.noPad }}
          body={[
            <PackageList type={PLAYER_PACKAGES} packages={playerPackages} />,
          ]}
        />
      ) : null}

      {filter?.includes?.("extras") && addonPackages?.length > 0 ? (
        <GSPageSection
          title="Add-Ons & Extras"
          style={style}
          bodyStyle={{ ...defaultPadding.noPad }}
          body={[
            <PackageList type={ADD_ON_PACKAGES} packages={addonPackages} />,
          ]}
        />
      ) : null}
    </>
  );

  const cartStepActions = [
    {
      title: "Continue",
      rightIcon: faArrowRight,
      type: "black",
      onClick: () => {
        const order = { ...registrationOrder };
        const activePaymentMethods = getActivePaymentMethods(
          permissions
        )?.map?.((apm) => apm.id);
        if (activePaymentMethods?.includes?.(1)) {
          order.paymentType = CREDIT_CARD;
        } else if (activePaymentMethods?.includes?.(2)) {
          order.paymentType = BANK_ACCOUNT;
        } else if (activePaymentMethods?.includes?.(4)) {
          order.paymentType = CUSTOMER_INVOICE;
        } else if (activePaymentMethods?.includes?.(3)) {
          order.paymentType = PAY_AT_COURSE;
        } else {
        }
        navigate(
          getRouting(eventBaseRoute, [
            tournament?.publicUrl,
            "cart",
            registrationOrder?.id,
          ])
        );
      },
    },
  ];

  const getItemList = () => {
    if (!packagesEnabled && !privateEvent) {
      return "";
    }
    if (activePackages?.length === 0 && !hasAccessPackages) {
      return (
        <div style={{ ...defaultLayouts.vStack }}>
          <PageHeader header="Packages" />
          {getEmptyListMessage()}
        </div>
      );
    }
    const itemCount = getOrderItemCount(registrationOrder);
    const price = getPriceFormatted(registrationOrder);
    return (
      <div style={{ ...defaultLayouts.vStack }}>
        <PageHeader header="Packages" />
        {packageSections}
        <PageFooter showFooter={itemCount > 0}>
          <CartStepItem
            nextStep="Next Step: Add Contact Details"
            description={getRegistrationSummary(registrationOrder, price)}
            style={{
              ...defaultLayouts.vStack,
              ...defaultLayouts.fullWidth,
              maxWidth: "1080px",
            }}
            actions={cartStepActions}
          />
        </PageFooter>
      </div>
    );
  };

  const { headerStyle } = useHeaderStyle();


  const notifications = useNotifications().filteredNotifications(["addTournamentPackageToOrderThunk"])

  const [banner] = useNotificationBanner({
    notifications: notifications,
    timeoutAction: () => {
      dispatch(clearNotifications());
    },
  });

  const getBanner = () => {
    return banner;
  };

  return (
    <event-packages>
      <GSListPage
      getBanner={ notifications?.length > 0 ? getBanner : false}
        getPageActions={getPageActions}
        getSearchActions={getSearchActions}
        getItemList={getItemList}
        isSidePanelOpen={isSidePanelOpen}
        getRouter={getRouter}
        headerStyle={headerStyle}
        loading={isLoading}
      ></GSListPage>
    </event-packages>
  );
};

export default EventPackages;

//package helpers--------------
export const isSponsorPackage = (p) => {
  return p.packageType === "sponsor" && p.state === ACTIVE;
};
export const isTeamPackage = (p) => {
  return (
    p.hasTeamPackageItem && p.packageType !== "sponsor" && p.state === ACTIVE
  );
};
export const isIndividualPackage = (p) => {
  return (
    p.hasPlayerPackageItem && p.packageType !== "sponsor" && p.state === ACTIVE
  );
};
export const isExtraPackage = (p) => {
  return (
    !p.hasPlayerPackageItem &&
    !p.hasTeamPackageItem &&
    p.packageType !== "sponsor" &&
    p.state === ACTIVE
  );
};

export const sortPackages = (tournamentPackages, tournament) => {
  let teamPackages = tournamentPackages?.filter?.((p) =>
    isTeamPackage(p, tournament)
  );
  let playerPackages = tournamentPackages?.filter?.((p) =>
    isIndividualPackage(p, tournament)
  );
  const sponsorPackages = tournamentPackages?.filter((p) =>
    isSponsorPackage(p)
  );
  const addonPackages = tournamentPackages?.filter((p) => isExtraPackage(p));
  if (tournament?.numberOfPlayers === 1) {
    playerPackages = [...playerPackages, ...teamPackages];
    teamPackages = [];
  }
  return { teamPackages, playerPackages, sponsorPackages, addonPackages };
};
